<script setup>
  import { useRoute, useRouter } from "vue-router";
  import { onMounted, onUnmounted, ref } from "vue";
  import { useFetch } from "@/composables/useFetch.js";
  import { uri } from "@/constants/config.js";
  import { Tab } from "@/base-components/Headless";
  import VMInformation from "./VMInformation.vue";
  import VMMetrics from "./VMMetrics.vue";
  import VMDiag from "./VMDiag.vue";
  import VMConsole from "./VMConsole.vue";
  import { useI18n } from "vue-i18n";
  import { toast } from "vue3-toastify";
  import Lucide from "@/base-components/Lucide";
  import AuditLogTable from "./AuditLogTable.vue";
  import moment from "moment-jalaali";
  import TrafficUsagePanel from "./TrafficUsagePanel.vue";
  import Billing from "./Billing.vue";

  const route = useRoute();
  const router = useRouter();
  const id = ref(route.params.id);
  const loadingFlag = ref(true);
  const loadingMetricsFlag = ref(true);
  const loadingDiagFlag = ref(true);
  const loadingConsoleFlag = ref(true);
  const isLoadingLogs = ref(true);
  const vmInformation = ref(null);
  const vmMetrics = ref(null);
  const vmDiag = ref(null);
  const vmConsole = ref(null);
  const auditLogs = ref([]);
  const { t: $t } = useI18n();

  moment.loadPersian({ usePersianDigits: true });

  const props = defineProps({
    role: {
      type: String,
      default: "",
    },
  });

  async function action(action) {
    const urls = {
      user: `${uri.api.iaas.instance.action}/${id.value}/action`,
      manager: `${uri.admin.iaas.instance.action}/${id.value}/action`,
      cloud: `${uri.admin.cloud.iaas.instance.action}/${id.value}/action`,
    };
    const url = urls[props.role];

    try {
      const { data, error } = await useFetch(url, {
        method: "POST",
        body: { action: action },
      });
      if (data) {
        console.log("data:", data);
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  }

  let timer = null;
  let isUnmounted = false;
  const getVM = async () => {
    const urls = {
      user: `${uri.api.iaas.instance.view}/${id.value}`,
      manager: `${uri.admin.iaas.instance.view}/${id.value}`,
      cloud: `${uri.admin.cloud.iaas.instance.view}/${id.value}`,
    };
    const url = urls[props.role];
    try {
      const { data, error } = await useFetch(url, { method: "GET" });
      if (data) {
        vmInformation.value = data;
        const delay =
          vmInformation.value.Interfaces.filter((x) => x.buildStatus < 2)
            .length ||
          vmInformation.value.Volumes.filter((x) => x.buildStatus < 2).length ||
          vmInformation.value.buildStatus < 2
            ? 1000
            : 5000;
        // )
        if (!isUnmounted)
          timer = setTimeout(() => {
            getVM();
          }, delay);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getVMMetrics = async () => {
    const urls = {
      user: `${uri.api.iaas.instance.view}/${id.value}/metrics`,
      manager: `${uri.admin.iaas.instance.view}/${id.value}/metrics`,
      cloud: `${uri.admin.cloud.iaas.instance.view}/${id.value}/metrics`,
    };
    const url = urls[props.role];

    loadingMetricsFlag.value = true;
    try {
      const { data, error } = await useFetch(url, { method: "GET" });
      if (data) {
        vmMetrics.value = data;
      }
    } catch (err) {
      console.log(err);
    }
    loadingMetricsFlag.value = false;
  };

  const getVMDiag = async () => {
    const urls = {
      user: `${uri.api.iaas.instance.view}/${id.value}/diag`,
      manager: `${uri.admin.iaas.instance.view}/${id.value}/diag`,
      cloud: `${uri.admin.cloud.iaas.instance.view}/${id.value}/diag`,
    };
    const url = urls[props.role];

    loadingDiagFlag.value = true;
    try {
      const { data, error } = await useFetch(url, { method: "GET" });
      if (data) {
        vmDiag.value = data;
      }
    } catch (err) {
      console.log(err);
    }
    loadingMetricsFlag.value = false;
  };

  const getVMConsole = async () => {
    const urls = {
      user: `${uri.api.iaas.instance.view}/${id.value}/console`,
      manager: `${uri.admin.iaas.instance.view}/${id.value}/console`,
      cloud: `${uri.admin.cloud.iaas.instance.view}/${id.value}/console`,
    };
    const url = urls[props.role];

    loadingConsoleFlag.value = true;
    try {
      const { data, error } = await useFetch(url, { method: "GET" });
      if (data) {
        vmConsole.value = data;
      }
    } catch (err) {
      console.log(err);
    }
    loadingConsoleFlag.value = false;
  };

  const fetchData = async () => {
    loadingFlag.value = true;
    try {
      await Promise.all([getVM(), getVMMetrics(), getVMDiag(), getVMConsole()]);
      fetchLogs();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      loadingFlag.value = false;
    }
  };

  async function changeIp(iface) {
    const urls = {
      user: uri.api.iaas.interface.resetIP(iface.id),
      manager: uri.admin.iaas.interface.resetIP(iface.id),
      cloud: uri.admin.cloud.iaas.interface.resetIP(iface.id),
    };
    const url = urls[props.role];

    loadingFlag.value = true;
    await useFetch(url, {
      method: "POST",
    });
    await getVM();
    loadingFlag.value = false;
    fetchLogs();
  }

  async function buyReserveTraffic(iface, plan) {
    if (!iface || !plan) return;
    loadingFlag.value = true;

    const urls = {
      user: uri.api.iaas.interface.buyTraffic(iface.id),
      manager: uri.admin.iaas.interface.buyTraffic(iface.id),
      cloud: uri.admin.cloud.iaas.interface.buyTraffic(iface.id),
    };

    const url = urls[props.role];

    await useFetch(url, {
      body: { trafficUnit: plan.key },
    });
    await getVM();
    loadingFlag.value = false;
    fetchLogs();
  }

  async function rebuild() {
    loadingFlag.value = true;

    const urls = {
      user: uri.api.iaas.instance.get(id.value) + "/rebuild",
      manager: uri.admin.iaas.instance.get(id.value) + "/rebuild",
      cloud: uri.admin.cloud.iaas.instance.get(id.value) + "/rebuild",
    };
    const url = urls[props.role];

    await useFetch(url, {
      method: "POST",
    });
    router.push(`/vm`);
  }

  async function deleteVm() {
    loadingFlag.value = true;
    const urls = {
      user: uri.api.iaas.instance.get(id.value),
      manager: uri.admin.iaas.instance.get(id.value),
      cloud: uri.admin.cloud.iaas.instance.get(id.value),
    };
    const url = urls[props.role];

    const { data } = await useFetch(url, {
      method: "DELETE",
    });
    if (data.status) {
      router.push(`/vm`).then(() => {
        toast($t("message.vm-deleted-success"), {
          theme: "auto",
          type: "success",
          dangerouslyHTMLString: true,
        });
      });
      return;
    }
    toast($t("message.vm-deleted-failed"), {
      theme: "auto",
      type: "error",
      dangerouslyHTMLString: true,
    });
    loadingFlag.value = false;
  }

  async function flavorUpdate(flavor) {
    loadingFlag.value = true;
    const urls = {
      user: uri.api.iaas.instance.get(id.value) + "/resize",
      manager: uri.admin.iaas.instance.get(id.value) + "/resize",
      cloud: uri.admin.cloud.iaas.instance.get(id.value) + "/resize",
    };
    const url = urls[props.role];

    const { data, error } = await useFetch(
      url,
      {
        body: {
          flavor: flavor.id,
        },
        method: "POST",
      },
      false
    );
    if (!data || error) {
      toast($t("message.operation-unsuccessful"), {
        theme: "auto",
        type: "error",
        dangerouslyHTMLString: true,
      });
      loadingFlag.value = false;
      return;
    }
    router.push(`/vm`).then(() => {
      toast($t("message.resize-start"), {
        theme: "auto",
        type: "success",
        dangerouslyHTMLString: true,
      });
    });
  }

  async function activeNic(nic) {
    loadingFlag.value = true;

    const urls = {
      user: uri.api.iaas.interface.active(nic.id),
      manager: uri.admin.iaas.interface.active(nic.id),
      cloud: uri.admin.cloud.iaas.interface.active(nic.id),
    };
    const url = urls[props.role];

    try {
      await useFetch(url, { method: "POST" });
      await getVM();
    } finally {
      loadingFlag.value = false;
    }
  }

  async function fetchLogs() {
    isLoadingLogs.value = true;

    const urls = {
      user: uri.api.iaas.instance.sub(id.value, "log"),
      manager: uri.admin.iaas.instance.sub(id.value, "log"),
      cloud: uri.admin.cloud.iaas.instance.sub(id.value, "log"),
    };
    const url = urls[props.role];

    const { data, error } = await useFetch(url, { method: "GET" }, false);
    auditLogs.value = !error && data ? data.data : [];
    isLoadingLogs.value = false;
  }

  // Fetch data when the component mounts
  onMounted(() => {
    fetchData();
  });
  onUnmounted(() => {
    isUnmounted = true;
    clearTimeout(timer);
  });
</script>

<template>
  <!-- BEGIN: Ticket -->
  <div class="grid grid-cols-12 gap-3 mt-3 intro-y">
    <!-- BEGIN: Ticket -->
    <Tab.Group class="col-span-12 lg:col-span-12">
      <div class="intro-y">
        <div class="box">
          <Tab.List variant="pills">
            <Tab>
              <Tab.Button as="button" class="w-full py-2">
                {{ $t("title.information") }}
              </Tab.Button>
            </Tab>
            <Tab>
              <Tab.Button as="button" class="w-full py-2">
                {{ $t("title.metrics") }}
              </Tab.Button>
            </Tab>
            <Tab>
              <Tab.Button as="button" class="w-full py-2">
                {{ $t("title.diag") }}
              </Tab.Button>
            </Tab>
            <Tab>
              <Tab.Button as="button" class="w-full py-2">
                {{ $t("title.console") }}
              </Tab.Button>
            </Tab>
            <Tab v-if="isManager">
              <Tab.Button as="button" class="w-full py-2">
                {{ $t("title.billing") }}
              </Tab.Button>
            </Tab>
          </Tab.List>
        </div>
      </div>
      <Tab.Panels>
        <Tab.Panel>
          <VMInformation
            :data="vmInformation"
            @action="action"
            @change-ip="changeIp"
            @buy-traffic="buyReserveTraffic"
            @flavor-update="flavorUpdate"
            @rebuild="rebuild"
            @delete-vm="deleteVm"
            @active-request="activeNic"
            :role="role"
            v-if="!loadingFlag && vmInformation"
          />
          <FreeStyleShimmer
            v-if="loadingFlag || !vmInformation"
            height="400px"
            width="100%"
            class="m-5"
          />
          <div class="col-span-12">
            <div
              class="relative before:content-[''] before:w-[95%] before:shadow-[0px_3px_5px_#0000000b] before:bg-white/60 before:h-full before:mt-2.5 before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
            >
              <div class="box">
                <div
                  class="flex flex-col justify-center col-span-12 px-8 py-12 lg:col-span-6"
                >
                  <Lucide icon="Logs" class="w-10 h-10 text-success" />
                  گزارش تغییرات
                  <div class="mt-5 relative">
                    <free-style-shimmer
                      v-if="isLoadingLogs"
                      v-for="i in [1, 2, 3]"
                      height="40px"
                      width="100%"
                      class="mb-3"
                    />
                    <audit-log-table
                      :is-loading="isLoadingLogs"
                      :audit-logs="auditLogs"
                    />
                  </div>
                </div>
                <div
                  class="col-span-12 p-8 border-t border-dashed lg:col-span-8 lg:border-t-0 lg:border-l border-slate-200 dark:border-darkmode-300"
                ></div>
              </div>
            </div>
          </div>
        </Tab.Panel>
        <Tab.Panel>
          <VMMetrics :data="vmMetrics" v-if="!loadingFlag && vmMetrics" />
          <FreeStyleShimmer
            v-if="loadingFlag"
            height="400px"
            width="100%"
            class="m-5"
          />
          <div class="col-span-12">
            <div
              class="relative before:content-[''] before:w-[95%] before:shadow-[0px_3px_5px_#0000000b] before:bg-white/60 before:h-full before:mt-2.5 before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
            >
              <div class="box">
                <div
                  class="flex flex-col justify-center col-span-12 px-8 py-12 lg:col-span-6"
                >
                  <Lucide
                    icon="TrafficCone"
                    class="w-10 h-10 text-success inline"
                  />
                  <span class="mr-1">گزارش مصرف</span>
                  <div class="mt-5 relative">
                    <TrafficUsagePanel :instance="vmInformation" :role="role" />
                  </div>
                </div>
                <div
                  class="col-span-12 p-8 border-t border-dashed lg:col-span-8 lg:border-t-0 lg:border-l border-slate-200 dark:border-darkmode-300"
                ></div>
              </div>
            </div>
          </div>
        </Tab.Panel>
        <Tab.Panel>
          <VMDiag :data="vmDiag" v-if="!loadingFlag && vmDiag" />
          <FreeStyleShimmer
            v-if="loadingFlag"
            height="400px"
            width="100%"
            class="m-5"
          />
        </Tab.Panel>
        <Tab.Panel>
          <VMConsole :data="vmConsole" v-if="!loadingFlag && vmConsole" />
          <FreeStyleShimmer
            v-if="loadingFlag"
            height="400px"
            width="100%"
            class="m-5"
          />
        </Tab.Panel>
        <Tab.Panel v-if="isManager">
          <Billing :data="vmInformation" v-if="!loadingFlag && vmInformation" />
          <FreeStyleShimmer
            v-if="loadingFlag"
            height="400px"
            width="100%"
            class="m-5"
          />
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
    <!-- END: Ticket -->
  </div>
</template>

<style scoped></style>
